import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp, updateDoc, increment, setDoc, deleteDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Helmet } from 'react-helmet-async';
import { db, auth, functions } from '../../firebase/config';

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');

  const [isFollowing, setIsFollowing] = useState(false);
  const [followingLoading, setFollowingLoading] = useState(false);

  // --- MARKET VALUE STATE ---
  const [marketData, setMarketData] = useState(null);

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const docRef = doc(db, 'listings', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const itemData = docSnap.data();
          setItem({ id: docSnap.id, ...itemData });

          if (!auth.currentUser || auth.currentUser.uid !== itemData.sellerId) {
            updateDoc(docRef, { views: increment(1) }).catch(e => console.error("View count error", e));
          }

          if (auth.currentUser) {
            const followRef = doc(db, `users/${itemData.sellerId}/followers`, auth.currentUser.uid);
            const followSnap = await getDoc(followRef);
            setIsFollowing(followSnap.exists());
          }

          // --- MARKET VALUE TRACKER ENGINE ---
          const soldQuery = query(collection(db, 'listings'), where('status', '==', 'sold'), where('category', '==', itemData.category));
          const soldSnap = await getDocs(soldQuery);
          
          let totalVal = 0;
          let count = 0;
          
          const mainKeyword = itemData.title.split(' ')[0].toLowerCase();

          soldSnap.forEach(d => {
            const soldItem = d.data();
            if (soldItem.title.toLowerCase().includes(mainKeyword)) {
              totalVal += Number(soldItem.price);
              count++;
            }
          });

          if (count > 0) {
            setMarketData({ average: (totalVal / count).toFixed(2), compsCount: count });
          }

        } else {
          console.error("Item not found");
        }
      } catch (error) {
        console.error("Error fetching item:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id]);

  const handlePayPalCheckout = async () => {
    if (!auth.currentUser) return alert("Please log in to purchase.");
    setProcessing(true);
    try {
      const createOrder = httpsCallable(functions, 'createPayPalOrder');
      const result = await createOrder({ itemId: item.id });
      
      if (result.data.url) {
        window.location.assign(result.data.url);
      } else {
        throw new Error("No redirect URL found from PayPal.");
      }
    } catch (error) {
      console.error(error);
      alert("PayPal Checkout failed.");
      setProcessing(false);
    }
  };

  const handleStartChat = async () => {
    if (!auth.currentUser) return alert("Please log in to message the seller.");
    if (auth.currentUser.uid === item.sellerId) return alert("You cannot message yourself!");

    try {
      const q = query(collection(db, 'chats'), where('itemId', '==', item.id), where('participants', 'array-contains', auth.currentUser.uid));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        await addDoc(collection(db, 'chats'), {
          itemId: item.id,
          itemTitle: item.title,
          participants: [auth.currentUser.uid, item.sellerId],
          updatedAt: serverTimestamp(),
          lastMessage: ""
        });
      }
      navigate('/inbox');
    } catch (error) {
      console.error("Error starting chat:", error);
      alert("Could not start conversation.");
    }
  };

  const handleSendOffer = async (e) => {
    e.preventDefault();
    if (!auth.currentUser) return alert("Please log in to make an offer.");
    if (auth.currentUser.uid === item.sellerId) return alert("You cannot make an offer on your own gear!");
    if (!offerAmount || isNaN(offerAmount) || Number(offerAmount) <= 0) return alert("Enter a valid dollar amount.");

    try {
      const q = query(collection(db, 'chats'), where('itemId', '==', item.id), where('participants', 'array-contains', auth.currentUser.uid));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        await addDoc(collection(db, 'chats'), {
          itemId: item.id,
          itemTitle: item.title,
          participants: [auth.currentUser.uid, item.sellerId],
          buyerId: auth.currentUser.uid,
          updatedAt: serverTimestamp(),
          lastMessage: ""
        });
      }

      sessionStorage.setItem('pendingOffer', `🚨 NEW OFFER: $${Number(offerAmount).toFixed(2)} for ${item.title}`);
      navigate('/inbox');
      
    } catch (error) {
      console.error("Error starting offer:", error);
      alert("Could not process offer.");
    }
  };

  const handleReportItem = async () => {
    if (!auth.currentUser) return alert("Please log in to report an item.");
    if (!window.confirm("Are you sure you want to flag this item? False reports may result in an account ban.")) return;

    try {
      const reportFn = httpsCallable(functions, 'reportListing');
      const response = await reportFn({ itemId: item.id });
      
      if (response.data.quarantined) {
        alert("This item has received multiple reports and has been automatically removed for administrative review.");
        navigate('/'); 
      } else {
        alert("Report submitted successfully. Our team will review this listing.");
      }
    } catch (error) {
      alert(error.message || "Failed to submit report.");
    }
  };

  const handleToggleFollow = async () => {
    if (!auth.currentUser) return alert("Please log in to follow sellers.");
    if (auth.currentUser.uid === item.sellerId) return alert("You can't follow yourself!");
    
    setFollowingLoading(true);
    const followRef = doc(db, `users/${item.sellerId}/followers`, auth.currentUser.uid);

    try {
      if (isFollowing) {
        await deleteDoc(followRef);
        setIsFollowing(false);
      } else {
        await setDoc(followRef, { 
          followerEmail: auth.currentUser.email,
          followedAt: serverTimestamp() 
        });
        setIsFollowing(true);
      }
    } catch (error) {
      console.error("Follow error:", error);
    } finally {
      setFollowingLoading(false);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: item.title,
      text: `Check out this ${item.title} on Nundinaria!`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#0f172a', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '18px' }}>Loading Gear...</h2>
          <p style={{ color: '#64748b' }}>Retrieving item details.</p>
        </div>
      </div>
    );
  }

  if (!item) return <div style={{ padding: '100px', textAlign: 'center', color: '#0f172a', fontSize: '20px', fontWeight: 'bold' }}>Item not found.</div>;
  if (item.status === 'quarantined') return <div style={{ padding: '100px', textAlign: 'center', color: '#ef4444', fontSize: '20px', fontWeight: 'bold' }}>This item has been removed for violating marketplace guidelines.</div>;

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: '40px 20px', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        
        {/* 🚨 SEO TAG INJECTION */}
        <Helmet>
          <title>{item.title} | Nundinaria</title>
          <meta name="description" content={item.description?.substring(0, 150)} />
          <meta property="og:title" content={`${item.title} - $${Number(item.price).toFixed(2)}`} />
          <meta property="og:description" content={item.description?.substring(0, 150)} />
          <meta property="og:image" content={item.mainImage || item.imageUrls?.[0] || 'https://via.placeholder.com/600'} />
          <meta property="og:url" content={window.location.href} />
          <meta property="og:type" content="product" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${item.title} - $${Number(item.price).toFixed(2)}`} />
          <meta name="twitter:description" content={item.description?.substring(0, 150)} />
          <meta name="twitter:image" content={item.mainImage || item.imageUrls?.[0] || 'https://via.placeholder.com/600'} />
        </Helmet>

        <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          
          {/* LEFT: Image Gallery with Glassmorphism */}
          <div style={{ flex: '1 1 450px', position: 'relative' }}>
            
            {/* Share Button Overlay */}
            <button 
              onClick={handleShare}
              style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 10, background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(8px)', border: 'none', borderRadius: '50%', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', fontSize: '20px', transition: 'transform 0.2s' }}
              onMouseOver={e => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
              title="Share Item"
            >
              ↗️
            </button>

            {/* Badges Overlay */}
            <div style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
              {item.isVerified && <div style={{ background: 'rgba(16, 185, 129, 0.9)', backdropFilter: 'blur(4px)', color: 'white', padding: '6px 14px', borderRadius: '50px', fontSize: '12px', fontWeight: '800', letterSpacing: '0.5px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>✓ AUTHENTIC</div>}
              {item.isFeatured && <div style={{ background: 'rgba(245, 158, 11, 0.9)', backdropFilter: 'blur(4px)', color: 'white', padding: '6px 14px', borderRadius: '50px', fontSize: '12px', fontWeight: '800', letterSpacing: '0.5px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>⭐ FEATURED</div>}
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '24px', overflow: 'hidden', height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)' }}>
              {/* 🚨 SEO: Enhanced Alt Tag & Fallback onError */}
              <img 
                src={item.mainImage || item.imageUrls?.[0] || 'https://via.placeholder.com/600'} 
                alt={`${item.condition || 'Used'} ${item.brand ? item.brand + ' ' : ''}${item.title}`} 
                style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#f1f5f9' }} 
                onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/600?text=Image+Unavailable'; }}
              />
            </div>
          </div>

          {/* RIGHT: Details & Actions */}
          <div style={{ flex: '1 1 450px', display: 'flex', flexDirection: 'column' }}>
            
            <div style={{ marginBottom: '16px' }}>
              <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>
                📍 {item.location || 'United States'}
              </span>
            </div>

            <h1 style={{ margin: '0 0 16px 0', fontSize: '36px', color: '#0f172a', fontWeight: '800', letterSpacing: '-1px', lineHeight: '1.2' }}>{item.title}</h1>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '24px' }}>
              <p style={{ fontSize: '36px', fontWeight: '900', color: '#0f172a', margin: 0, letterSpacing: '-1px' }}>${Number(item.price).toFixed(2)}</p>
              
              {/* --- GAMIFICATION BADGES --- */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {item.sellerBadges?.includes('Fast Shipper') && (
                  <span style={{ fontSize: '12px', color: '#ea580c', backgroundColor: '#fff7ed', padding: '6px 12px', borderRadius: '8px', fontWeight: '700', border: '1px solid #ffedd5' }}>
                    ⚡ FAST SHIPPER
                  </span>
                )}
                {item.sellerBadges?.includes('Hardware Guru') && (
                  <span style={{ fontSize: '12px', color: '#059669', backgroundColor: '#ecfdf5', padding: '6px 12px', borderRadius: '8px', fontWeight: '700', border: '1px solid #d1fae5' }}>
                    🧠 HARDWARE GURU
                  </span>
                )}
              </div>
            </div>

            {/* --- MARKET VALUE TRACKER ENGINE --- */}
            {marketData && (
              <div style={{ 
                backgroundColor: Number(item.price) <= marketData.average ? '#f0fdf4' : '#fef3c7', 
                padding: '12px 16px', 
                borderRadius: '12px', 
                border: `1px solid ${Number(item.price) <= marketData.average ? '#bbf7d0' : '#fde68a'}`,
                marginBottom: '24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <span style={{ display: 'block', fontSize: '11px', color: Number(item.price) <= marketData.average ? '#065f46' : '#92400e', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    📈 Nundinaria Market Value
                  </span>
                  <span style={{ fontSize: '15px', color: Number(item.price) <= marketData.average ? '#065f46' : '#92400e', fontWeight: '800' }}>
                    Based on {marketData.compsCount} recently sold items
                  </span>
                </div>
                <span style={{ fontSize: '18px', fontWeight: '900', color: Number(item.price) <= marketData.average ? '#059669' : '#d97706' }}>
                  Avg: ${marketData.average}
                </span>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '32px' }}>
              <span style={{ backgroundColor: '#f1f5f9', color: '#475569', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', border: '1px solid #e2e8f0' }}>Condition: {item.condition || 'Used'}</span>
              {item.brand && <span style={{ backgroundColor: '#e0e7ff', color: '#4f46e5', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700' }}>Brand: {item.brand}</span>}
              {!item.shippingAvailable ? (
                <span style={{ backgroundColor: 'white', color: '#0f172a', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: '1px solid #cbd5e1' }}>🤝 Local Pickup Only</span>
              ) : (
                <span style={{ backgroundColor: '#0f172a', color: 'white', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700' }}>✈️ Shipping Available</span>
              )}
            </div>

            <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '20px', border: '1px solid #e2e8f0', marginBottom: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02)' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>Item Description</h3>
              <p style={{ margin: 0, color: '#475569', lineHeight: '1.7', whiteSpace: 'pre-wrap', fontSize: '15px' }}>{item.description}</p>
            </div>

            {/* --- SELLER CARD --- */}
            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '20px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02)' }}>
              <div>
                <p style={{ margin: '0 0 4px 0', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sold By</p>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', color: '#0f172a' }}>{item.sellerEmail?.split('@')[0]}</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: '#f59e0b', fontSize: '14px', letterSpacing: '1px' }}>★★★★★</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>{item.sellerRating || 'New Seller'}</span>
                </div>
              </div>
              <button 
                onClick={handleToggleFollow} 
                disabled={followingLoading}
                style={{ padding: '10px 20px', borderRadius: '12px', border: isFollowing ? '1px solid #cbd5e1' : 'none', backgroundColor: isFollowing ? 'white' : '#0f172a', color: isFollowing ? '#475569' : 'white', fontWeight: '700', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease' }}
              >
                {isFollowing ? '✓ Following' : '+ Follow Seller'}
              </button>
            </div>

            {/* --- GOOGLE MAPS LOCATION RADIUS --- */}
            <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', padding: '24px', borderRadius: '20px', marginBottom: '32px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02)' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#0f172a', fontWeight: '700' }}>
                📍 Approximate Location
              </h3>
              <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '14px' }}>
                {item.location || 'Location not specified'}
              </p>
              <div style={{ width: '100%', height: '220px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <iframe
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  scrolling="no"
                  marginHeight="0"
                  marginWidth="0"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(item.location || 'United States')}&t=&z=11&ie=UTF8&iwloc=&output=embed`}
                  title="Seller Location Map"
                ></iframe>
              </div>
            </div>

            {/* --- ACTION BUTTONS --- */}
            {item.status === 'active' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: 'auto' }}>
                
                {!item.shippingAvailable && (
                  <div style={{ backgroundColor: '#f0fdf4', padding: '20px', borderRadius: '16px', border: '1px solid #bbf7d0', marginBottom: '8px' }}>
                    <h4 style={{ margin: '0 0 8px 0', color: '#065f46', fontSize: '15px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      🛡️ Safe Local Exchange
                    </h4>
                    <p style={{ margin: '0 0 12px 0', color: '#047857', fontSize: '14px', lineHeight: '1.5' }}>
                      This item is local pickup only. We strongly advise meeting at a verified safe public location.
                    </p>
                    <ul style={{ margin: 0, paddingLeft: '20px', color: '#065f46', fontSize: '13px', fontWeight: '600', lineHeight: '1.6' }}>
                      <li>Elkhart Police Department Lobby (175 Waterfall Dr)</li>
                      <li>Well-lit Martin's Super Market</li>
                      <li>Public Library branches during daylight</li>
                    </ul>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '16px' }}>
                  <button onClick={handleStartChat} style={{ flex: 1, padding: '16px', backgroundColor: 'white', color: '#0f172a', border: '2px solid #cbd5e1', borderRadius: '12px', fontWeight: '700', fontSize: '16px', cursor: 'pointer', transition: 'border-color 0.2s' }}
                    onMouseOver={e => e.currentTarget.style.borderColor = '#0f172a'}
                    onMouseOut={e => e.currentTarget.style.borderColor = '#cbd5e1'}
                  >
                    💬 Message Seller
                  </button>
                  <button onClick={() => setShowOfferModal(true)} style={{ flex: 1, padding: '16px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', fontSize: '16px', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(245, 158, 11, 0.3)', transition: 'background-color 0.2s' }}
                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#d97706'}
                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#f59e0b'}
                  >
                    🤝 Make Offer
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', margin: '8px 0' }}>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }}></div>
                  <span style={{ padding: '0 16px', color: '#94a3b8', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Or Buy Now</span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }}></div>
                </div>

                <button onClick={handlePayPalCheckout} disabled={processing} style={{ width: '100%', padding: '16px', backgroundColor: '#ffc439', color: '#003087', border: 'none', borderRadius: '12px', fontWeight: '900', fontSize: '18px', cursor: processing ? 'not-allowed' : 'pointer', fontStyle: 'italic', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                  Pay<span style={{ color: '#0079c1' }}>Pal</span> Checkout
                </button>
                
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                  <button onClick={handleReportItem} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'opacity 0.2s' }}
                    onMouseOver={e => e.currentTarget.style.opacity = '0.7'}
                    onMouseOut={e => e.currentTarget.style.opacity = '1'}
                  >
                    🚩 Report this listing
                  </button>
                </div>

              </div>
            ) : (
              <div style={{ marginTop: 'auto', padding: '24px', backgroundColor: '#f1f5f9', borderRadius: '16px', textAlign: 'center', fontWeight: '700', color: '#475569', fontSize: '18px', border: '1px solid #e2e8f0' }}>
                This item has been sold.
              </div>
            )}

          </div>
        </div>

        {/* --- OFFER MODAL --- */}
        {showOfferModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
            <div style={{ backgroundColor: 'white', padding: '32px', borderRadius: '24px', width: '100%', maxWidth: '420px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '24px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.5px' }}>Negotiate Price</h3>
              <p style={{ margin: '0 0 24px 0', color: '#64748b', fontSize: '15px', lineHeight: '1.5' }}>Send a binding offer for <strong>{item.title}</strong>. If the seller accepts, you can check out immediately.</p>
              
              <form onSubmit={handleSendOffer}>
                <div style={{ position: 'relative', marginBottom: '24px' }}>
                  <span style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', fontSize: '24px', color: '#475569', fontWeight: '800' }}>$</span>
                  <input type="number" value={offerAmount} onChange={(e) => setOfferAmount(e.target.value)} placeholder="0.00" autoFocus style={{ width: '100%', padding: '16px 16px 16px 45px', borderRadius: '12px', border: '2px solid #e2e8f0', fontSize: '28px', color: '#0f172a', fontWeight: '800', boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.2s' }} 
                    onFocus={e => e.target.style.borderColor = '#4f46e5'}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" onClick={() => setShowOfferModal(false)} style={{ flex: 1, padding: '14px', backgroundColor: 'transparent', color: '#475569', border: '1px solid #cbd5e1', borderRadius: '12px', fontWeight: '700', fontSize: '15px', cursor: 'pointer' }}>Cancel</button>
                  <button type="submit" style={{ flex: 1, padding: '14px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.3)' }}>Send Offer</button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}